/**
 * The single axios instance for the whole app (ARCHITECTURE.md §2 — "axios, single instance
 * w/ interceptors").
 *
 * Responsibilities:
 *  1. Attach `Authorization: Bearer <access_token>` to every request.
 *  2. On a 401 caused by an expired access token, refresh ONCE, queue every other request that
 *     failed in the meantime, and replay them with the new token. If the refresh fails, hard
 *     logout.
 *  3. Normalise every failure — the `{detail:{code,message,fields}}` envelope, FastAPI's
 *     `{detail: "..."}`, pydantic's `{detail:[...]}`, non-JSON bodies and network errors — into
 *     one typed {@link ApiError}.
 *
 * This module must NOT import the auth store: the store imports the API client. The store wires
 * itself in through {@link registerAuthBridge}; until then a default bridge reads localStorage
 * directly so the very first request of a page load already carries a token.
 */

import axios, {
  AxiosError,
  AxiosHeaders,
  type AxiosInstance,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from 'axios';

import type { ApiErrorCode, ApiErrorDetail, AuthResponse } from '@/types/api';

declare module 'axios' {
  // Custom per-request flags carried through the interceptor chain.
  export interface AxiosRequestConfig {
    /** Opt a request out of the silent-refresh dance (login / register / refresh). */
    skipAuthRefresh?: boolean;
    /** Set internally once a request has already been replayed after a refresh. */
    _hopebotRetried?: boolean;
  }
}

/* -------------------------------------------------------------------------- */
/* Storage keys — shared with the auth store                                  */
/* -------------------------------------------------------------------------- */

export const STORAGE_KEYS = {
  accessToken: 'hopebot.access_token',
  refreshToken: 'hopebot.refresh_token',
  user: 'hopebot.user',
  // v2: "nothing stored" now means "all accounts" rather than "the first one", so an id picked
  // back when a single account was the only option is not carried over.
  activeAccount: 'hopebot.active_account.v2',
} as const;

/** Every localStorage access is wrapped: private-mode / disabled-storage browsers throw. */
export const safeStorage = {
  get(key: string): string | null {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key: string, value: string): void {
    try {
      window.localStorage.setItem(key, value);
    } catch {
      /* quota exceeded or storage disabled — tokens stay in memory only */
    }
  },
  remove(key: string): void {
    try {
      window.localStorage.removeItem(key);
    } catch {
      /* no-op */
    }
  },
};

/* -------------------------------------------------------------------------- */
/* ApiError                                                                   */
/* -------------------------------------------------------------------------- */

export interface ApiErrorInit {
  code: ApiErrorCode;
  message: string;
  status: number;
  fields?: Record<string, string> | null;
  cause?: unknown;
}

/**
 * The only error type the app's data layer ever throws.
 * `message` is the server's human-readable text; the UI should prefer `errors.<code>` from i18n
 * and fall back to `message`.
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  /** HTTP status, or 0 when the request never reached the server. */
  readonly status: number;
  /** Field-level validation messages keyed by request field name. Always an object. */
  readonly fields: Record<string, string>;
  readonly cause?: unknown;

  constructor(init: ApiErrorInit) {
    super(init.message);
    this.name = 'ApiError';
    this.code = init.code;
    this.status = init.status;
    this.fields = init.fields ?? {};
    this.cause = init.cause;
    Object.setPrototypeOf(this, ApiError.prototype);
  }

  static is(value: unknown): value is ApiError {
    return value instanceof ApiError;
  }

  /** `true` for 4xx — these are the responses we never retry. */
  get isClientError(): boolean {
    return this.status >= 400 && this.status < 500;
  }

  get isNetworkError(): boolean {
    return this.code === 'NETWORK_ERROR';
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  /** True when the error carries per-field messages a form can surface. */
  get hasFieldErrors(): boolean {
    return Object.keys(this.fields).length > 0;
  }

  /** i18n key for this error, e.g. `errors.INSUFFICIENT_FUNDS`. */
  get i18nKey(): string {
    return `errors.${this.code}`;
  }
}

const STATUS_TO_CODE: Record<number, ApiErrorCode> = {
  400: 'VALIDATION_ERROR',
  401: 'UNAUTHORIZED',
  403: 'FORBIDDEN',
  404: 'NOT_FOUND',
  409: 'CONFLICT',
  422: 'VALIDATION_ERROR',
  429: 'RATE_LIMITED',
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function coerceCode(value: unknown, status: number): ApiErrorCode {
  if (typeof value === 'string' && value.length > 0) return value as ApiErrorCode;
  return STATUS_TO_CODE[status] ?? 'INTERNAL_ERROR';
}

function coerceFields(value: unknown): Record<string, string> | null {
  if (!isRecord(value)) return null;
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value)) {
    if (typeof raw === 'string') out[key] = raw;
    else if (raw !== null && raw !== undefined) out[key] = String(raw);
  }
  return Object.keys(out).length > 0 ? out : null;
}

/**
 * Pydantic v2 / FastAPI 422 shape:
 * `{ detail: [{ loc: ["body","base_order_size"], msg: "...", type: "..." }] }`
 */
function fieldsFromPydantic(detail: unknown[]): Record<string, string> | null {
  const out: Record<string, string> = {};
  for (const entry of detail) {
    if (!isRecord(entry)) continue;
    const loc = entry.loc;
    const msg = typeof entry.msg === 'string' ? entry.msg : null;
    if (!msg) continue;
    const name = Array.isArray(loc)
      ? String(loc[loc.length - 1] ?? '')
      : typeof loc === 'string'
        ? loc
        : '';
    if (name) out[name] = msg;
  }
  return Object.keys(out).length > 0 ? out : null;
}

/** Normalise anything axios can throw into an {@link ApiError}. */
export function normalizeError(error: unknown): ApiError {
  if (ApiError.is(error)) return error;

  if (axios.isCancel(error)) {
    return new ApiError({
      code: 'NETWORK_ERROR',
      message: 'Request cancelled',
      status: 0,
      cause: error,
    });
  }

  if (error instanceof AxiosError) {
    const response = error.response;

    // No response at all: offline, DNS failure, CORS rejection, timeout.
    if (!response) {
      return new ApiError({
        code: 'NETWORK_ERROR',
        message: error.message || 'Network request failed',
        status: 0,
        cause: error,
      });
    }

    const status = response.status;
    const body: unknown = response.data;

    // Canonical envelope: { detail: { code, message, fields } }
    if (isRecord(body) && isRecord(body.detail)) {
      const detail = body.detail as unknown as Partial<ApiErrorDetail>;
      return new ApiError({
        code: coerceCode(detail.code, status),
        message:
          typeof detail.message === 'string' && detail.message
            ? detail.message
            : error.message || `HTTP ${status}`,
        status,
        fields: coerceFields(detail.fields),
        cause: error,
      });
    }

    // FastAPI HTTPException with a plain string detail.
    if (isRecord(body) && typeof body.detail === 'string') {
      return new ApiError({
        code: coerceCode(undefined, status),
        message: body.detail,
        status,
        cause: error,
      });
    }

    // Pydantic validation array.
    if (isRecord(body) && Array.isArray(body.detail)) {
      return new ApiError({
        code: 'VALIDATION_ERROR',
        message: 'Validation failed',
        status,
        fields: fieldsFromPydantic(body.detail),
        cause: error,
      });
    }

    // Non-JSON body (HTML error page from a proxy, empty body, plain text...).
    const text = typeof body === 'string' && body.trim() ? body.trim().slice(0, 300) : '';
    return new ApiError({
      code: coerceCode(undefined, status),
      message: text || error.message || `HTTP ${status}`,
      status,
      cause: error,
    });
  }

  if (error instanceof Error) {
    return new ApiError({
      code: 'INTERNAL_ERROR',
      message: error.message,
      status: 0,
      cause: error,
    });
  }

  return new ApiError({ code: 'INTERNAL_ERROR', message: 'Unknown error', status: 0, cause: error });
}

/* -------------------------------------------------------------------------- */
/* Auth bridge — filled in by the zustand store, defaults to localStorage      */
/* -------------------------------------------------------------------------- */

export interface AuthBridge {
  getAccessToken(): string | null;
  getRefreshToken(): string | null;
  /** Called after a successful silent refresh. */
  onRefreshed(auth: AuthResponse): void;
  /** Called when the refresh fails — wipe state and bounce to /login. */
  onAuthFailure(): void;
  /** Another tab already put a new pair in shared storage — take it over. */
  syncFromStorage(): void;
}

const defaultBridge: AuthBridge = {
  getAccessToken: () => safeStorage.get(STORAGE_KEYS.accessToken),
  getRefreshToken: () => safeStorage.get(STORAGE_KEYS.refreshToken),
  onRefreshed: (auth) => {
    safeStorage.set(STORAGE_KEYS.accessToken, auth.access_token);
    safeStorage.set(STORAGE_KEYS.refreshToken, auth.refresh_token);
  },
  onAuthFailure: () => {
    safeStorage.remove(STORAGE_KEYS.accessToken);
    safeStorage.remove(STORAGE_KEYS.refreshToken);
    safeStorage.remove(STORAGE_KEYS.user);
  },
  // This bridge reads storage on every call, so there is nothing to take over.
  syncFromStorage: () => undefined,
};

let bridge: AuthBridge = defaultBridge;

/** Called once by the auth store so the interceptors read/write live store state. */
export function registerAuthBridge(next: AuthBridge): void {
  bridge = next;
}

export function getAccessToken(): string | null {
  return bridge.getAccessToken();
}

/* -------------------------------------------------------------------------- */
/* Axios instances                                                            */
/* -------------------------------------------------------------------------- */

export const API_BASE_URL: string = import.meta.env.VITE_API_URL ?? '/api/v1';

const COMMON_CONFIG = {
  baseURL: API_BASE_URL,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
};

/**
 * Bare client with NO interceptors — used for `/auth/refresh` so a failing refresh can never
 * recurse back into the refresh logic.
 */
const rawClient: AxiosInstance = axios.create(COMMON_CONFIG);

export const api: AxiosInstance = axios.create(COMMON_CONFIG);

/** Request config plus our own flags (declared via the `axios` module augmentation above). */
export type HopeBotRequestConfig = InternalAxiosRequestConfig;

/** Endpoints whose 401 means "bad credentials", not "expired token". */
const NO_REFRESH_PATHS = ['/auth/login', '/auth/register', '/auth/refresh'];

function isNoRefreshPath(url: string | undefined): boolean {
  if (!url) return false;
  return NO_REFRESH_PATHS.some((path) => url.includes(path));
}

/* --- request interceptor --------------------------------------------------- */

api.interceptors.request.use((config) => {
  const token = bridge.getAccessToken();
  if (token) {
    const headers = AxiosHeaders.from(config.headers);
    headers.set('Authorization', `Bearer ${token}`);
    config.headers = headers;
  }
  return config;
});

/* --- refresh single-flight + replay queue ---------------------------------- */

let refreshPromise: Promise<string> | null = null;

/** Web Locks name that makes the tabs of one browser refresh one at a time. */
const REFRESH_LOCK = 'hopebot.auth.refresh';

/**
 * The access token another tab has already put in shared storage in place of `failedToken`, or
 * null. Every tab keeps its own copy of the tokens: a tab that refreshed with its copy after
 * another tab had rotated the pair would send a spent refresh token, and the server answers that
 * by ending every session of the user.
 */
function rotatedElsewhere(failedToken: string | null): string | null {
  const stored = safeStorage.get(STORAGE_KEYS.accessToken);
  if (stored === null || stored === failedToken) return null;
  bridge.syncFromStorage();
  return stored;
}

async function requestNewPair(failedToken: string | null): Promise<string> {
  const settled = rotatedElsewhere(failedToken);
  if (settled) return settled;

  const refreshToken = bridge.getRefreshToken();
  if (!refreshToken) {
    throw new ApiError({ code: 'UNAUTHORIZED', message: 'No refresh token', status: 401 });
  }

  try {
    const response = await rawClient.post<AuthResponse>('/auth/refresh', {
      refresh_token: refreshToken,
    });
    bridge.onRefreshed(response.data);
    return response.data.access_token;
  } catch (error) {
    // Without Web Locks two tabs can still send the same token at once. The server refuses the
    // later one without ending the session, and the earlier one's pair is then in storage.
    const late = rotatedElsewhere(failedToken);
    if (late) return late;
    throw error;
  }
}

/** One refresh at a time across all tabs: the next tab in line finds the new pair in storage. */
async function performRefresh(failedToken: string | null): Promise<string> {
  if (typeof navigator !== 'undefined' && 'locks' in navigator) {
    return await navigator.locks.request(REFRESH_LOCK, () => requestNewPair(failedToken));
  }
  return await requestNewPair(failedToken);
}

/**
 * All concurrent 401s await the SAME promise, so `/auth/refresh` is called exactly once.
 * Everyone who awaited it then replays their original request with the fresh token.
 */
function refreshOnce(failedToken: string | null): Promise<string> {
  if (!refreshPromise) {
    refreshPromise = performRefresh(failedToken).finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

/** The access token a request went out with, read back from its `Authorization` header. */
function sentToken(config: HopeBotRequestConfig): string | null {
  const header = AxiosHeaders.from(config.headers).get('Authorization');
  return typeof header === 'string' && header.startsWith('Bearer ') ? header.slice(7) : null;
}

/* --- response interceptor -------------------------------------------------- */

api.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: unknown) => {
    if (!(error instanceof AxiosError)) {
      return Promise.reject(normalizeError(error));
    }

    const config = error.config as HopeBotRequestConfig | undefined;
    const status = error.response?.status;

    const refreshable =
      status === 401 &&
      config !== undefined &&
      config._hopebotRetried !== true &&
      config.skipAuthRefresh !== true &&
      !isNoRefreshPath(config.url) &&
      bridge.getRefreshToken() !== null;

    if (!refreshable) {
      return Promise.reject(normalizeError(error));
    }

    try {
      const accessToken = await refreshOnce(sentToken(config));
      config._hopebotRetried = true;
      const headers = AxiosHeaders.from(config.headers);
      headers.set('Authorization', `Bearer ${accessToken}`);
      config.headers = headers;
      return await api.request(config);
    } catch (refreshError) {
      // Refresh itself failed → the session is unrecoverable. Hard logout.
      bridge.onAuthFailure();
      const normalized = normalizeError(refreshError);
      return Promise.reject(
        new ApiError({
          code: 'UNAUTHORIZED',
          message: normalized.message,
          status: 401,
          cause: refreshError,
        }),
      );
    }
  },
);

/* -------------------------------------------------------------------------- */
/* Thin typed helpers — every feature hook goes through these                  */
/* -------------------------------------------------------------------------- */

export interface RequestOptions {
  signal?: AbortSignal;
  params?: Record<string, unknown>;
  skipAuthRefresh?: boolean;
  /** Milliseconds; the instance default (30 s) when absent. */
  timeout?: number;
  /** Extra headers, e.g. `multipart/form-data` for a `FormData` body (the browser adds the
   * boundary; the instance default is JSON). */
  headers?: Record<string, string>;
}

function toAxiosConfig(options?: RequestOptions) {
  if (!options) return undefined;
  return {
    signal: options.signal,
    params: options.params,
    skipAuthRefresh: options.skipAuthRefresh,
    ...(options.timeout !== undefined ? { timeout: options.timeout } : {}),
    ...(options.headers ? { headers: options.headers } : {}),
  };
}

export const http = {
  async get<T>(url: string, options?: RequestOptions): Promise<T> {
    try {
      const res = await api.get<T>(url, toAxiosConfig(options));
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },
  /** `get`, with the response's headers (names in lower case) beside the body. */
  async getWithHeaders<T>(
    url: string,
    options?: RequestOptions,
  ): Promise<{ data: T; headers: Record<string, string | undefined> }> {
    try {
      const res = await api.get<T>(url, toAxiosConfig(options));
      return { data: res.data, headers: res.headers as Record<string, string | undefined> };
    } catch (error) {
      throw normalizeError(error);
    }
  },
  async post<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    try {
      const res = await api.post<T>(url, body, toAxiosConfig(options));
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },
  async patch<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    try {
      const res = await api.patch<T>(url, body, toAxiosConfig(options));
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },
  async put<T>(url: string, body?: unknown, options?: RequestOptions): Promise<T> {
    try {
      const res = await api.put<T>(url, body, toAxiosConfig(options));
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },
  async delete<T = void>(url: string, options?: RequestOptions): Promise<T> {
    try {
      const res = await api.delete<T>(url, toAxiosConfig(options));
      return res.data;
    } catch (error) {
      throw normalizeError(error);
    }
  },
};

/**
 * Strip `undefined`/`null`/`''` entries so we never send `?status=undefined`.
 * Every list hook funnels its filters through this.
 */
export function cleanParams<T extends Record<string, unknown>>(params: T): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    out[key] = value;
  }
  return out;
}

export default api;
